const {
  Document,
  Paragraph,
  TextRun,
  HeadingLevel,
  Packer,
  Table,
  TableRow,
  TableCell,
  WidthType,
  ImageRun,
} = require("docx");

function colorToHex(colorStr) {
  if (!colorStr) return null;
  const str = colorStr.trim();
  if (str.startsWith("#")) {
    const hex = str.replace("#", "");
    if (hex.length === 3) {
      return hex.split("").map((c) => c + c).join("").toUpperCase();
    }
    return hex.slice(0, 6).toUpperCase();
  }
  const rgbMatch = str.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10).toString(16).padStart(2, "0");
    const g = parseInt(rgbMatch[2], 10).toString(16).padStart(2, "0");
    const b = parseInt(rgbMatch[3], 10).toString(16).padStart(2, "0");
    return `${r}${g}${b}`.toUpperCase();
  }
  return null;
}

async function getImageDataAndDimensions(src, specifiedWidth, specifiedHeight) {
  if (!src) return null;
  try {
    let uint8Array = null;

    if (typeof src === "string" && src.startsWith("data:")) {
      const base64Index = src.indexOf("base64,");
      if (base64Index !== -1) {
        const base64Str = src.substring(base64Index + 7);
        const binaryString = window.atob(base64Str);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        uint8Array = bytes;
      }
    }

    if (!uint8Array) {
      const res = await fetch(src, { mode: "cors" });
      const blob = await res.blob();
      const buffer = await blob.arrayBuffer();
      uint8Array = new Uint8Array(buffer);
    }

    let width = 300;
    let height = 200;

    const dims = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
      img.onerror = () => resolve(null);
      img.src = src;
    });

    const origW = dims && dims.width > 0 ? dims.width : parseFloat(specifiedWidth) || 300;
    const origH = dims && dims.height > 0 ? dims.height : parseFloat(specifiedHeight) || 200;

    const maxW = 450;
    const maxH = 340;
    let calcW = origW;
    let calcH = origH;

    if (calcW > maxW) {
      calcH = Math.round((maxW / calcW) * calcH);
      calcW = maxW;
    }
    if (calcH > maxH) {
      calcW = Math.round((maxH / calcH) * calcW);
      calcH = maxH;
    }

    width = Math.max(Math.round(calcW), 30);
    height = Math.max(Math.round(calcH), 30);

    return { uint8Array, width, height };
  } catch (err) {
    console.warn("Failed to load image for Word document:", err);
    return null;
  }
}

async function parseHtmlToDocxElements(htmlContent) {
  if (!htmlContent) return [new Paragraph({ children: [new TextRun("")] })];

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${htmlContent}</div>`, "text/html");
  const root = doc.body.children[0] || doc.body;
  const elements = [];

  async function processInlineNodes(node, currentStyle = {}) {
    const runs = [];

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text) {
        runs.push(
          new TextRun({
            text: text,
            bold: currentStyle.bold || false,
            italics: currentStyle.italic || false,
            underline: currentStyle.underline ? {} : undefined,
            color: currentStyle.color || "28283C",
            size: currentStyle.size || 22, // 11pt (22 half-points)
            font: currentStyle.font || "Calibri",
          })
        );
      }
      return runs;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return runs;

    const el = node;
    const tagName = el.tagName.toLowerCase();

    if (tagName === "img") {
      const src = el.getAttribute("src");
      if (src) {
        const imgData = await getImageDataAndDimensions(src, el.getAttribute("width"), el.getAttribute("height"));
        if (imgData && imgData.uint8Array) {
          runs.push(
            new ImageRun({
              data: imgData.uint8Array,
              transformation: {
                width: imgData.width,
                height: imgData.height,
              },
            })
          );
        }
      }
      return runs;
    }

    const newStyle = { ...currentStyle };

    if (tagName === "strong" || tagName === "b") {
      newStyle.bold = true;
    }
    if (tagName === "em" || tagName === "i") {
      newStyle.italic = true;
    }
    if (tagName === "u") {
      newStyle.underline = true;
    }
    if (tagName === "mark") {
      newStyle.bold = true;
      newStyle.color = "3D2B90";
    }
    if (tagName === "code") {
      newStyle.font = "Consolas";
      newStyle.color = "3D2B90";
    }

    if (el.style) {
      if (el.style.color) {
        const hex = colorToHex(el.style.color);
        if (hex) newStyle.color = hex;
      }
      if (el.style.fontWeight === "bold" || parseInt(el.style.fontWeight, 10) >= 600) {
        newStyle.bold = true;
      }
      if (el.style.fontStyle === "italic") {
        newStyle.italic = true;
      }
      if (el.style.fontSize) {
        const pt = parseFloat(el.style.fontSize);
        if (pt > 0) newStyle.size = Math.round(pt * 2);
      }
    }

    if (tagName === "br") {
      runs.push(new TextRun({ break: 1 }));
      return runs;
    }

    for (const child of Array.from(el.childNodes)) {
      const childRuns = await processInlineNodes(child, newStyle);
      runs.push(...childRuns);
    }

    return runs;
  }

  async function processBlockNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const txt = node.textContent?.trim();
      if (txt) {
        elements.push(
          new Paragraph({
            children: [new TextRun({ text: txt, size: 22, font: "Calibri", color: "28283C" })],
            spacing: { after: 160 },
          })
        );
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node;
    const tagName = el.tagName.toLowerCase();

    if (tagName === "img") {
      const src = el.getAttribute("src");
      if (src) {
        const imgData = await getImageDataAndDimensions(src, el.getAttribute("width"), el.getAttribute("height"));
        if (imgData && imgData.uint8Array) {
          elements.push(
            new Paragraph({
              children: [
                new ImageRun({
                  data: imgData.uint8Array,
                  transformation: {
                    width: imgData.width,
                    height: imgData.height,
                  },
                }),
              ],
              spacing: { before: 100, after: 140 },
            })
          );
        }
      }
      return;
    }

    if (tagName === "h1") {
      elements.push(
        new Paragraph({
          children: await processInlineNodes(el, { bold: true, color: "3D2B90", size: 36 }),
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 240, after: 120 },
        })
      );
      return;
    }
    if (tagName === "h2") {
      elements.push(
        new Paragraph({
          children: await processInlineNodes(el, { bold: true, color: "3D2B90", size: 30 }),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 },
        })
      );
      return;
    }
    if (tagName === "h3") {
      elements.push(
        new Paragraph({
          children: await processInlineNodes(el, { bold: true, color: "3D2B90", size: 26 }),
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 160, after: 80 },
        })
      );
      return;
    }
    if (tagName === "h4" || tagName === "h5" || tagName === "h6") {
      elements.push(
        new Paragraph({
          children: await processInlineNodes(el, { bold: true, color: "3D2B90", size: 24 }),
          heading: HeadingLevel.HEADING_4,
          spacing: { before: 140, after: 60 },
        })
      );
      return;
    }

    async function processList(listEl, listLevel = 0) {
      const isOrdered = listEl.tagName.toLowerCase() === "ol";
      const items = Array.from(listEl.children).filter(
        (c) => c.tagName && c.tagName.toLowerCase() === "li"
      );

      for (let i = 0; i < items.length; i++) {
        const li = items[i];
        const nestedLists = [];
        const inlineNodes = [];

        for (const child of Array.from(li.childNodes)) {
          if (
            child.nodeType === Node.ELEMENT_NODE &&
            ["ul", "ol"].includes(child.tagName.toLowerCase())
          ) {
            nestedLists.push(child);
          } else {
            inlineNodes.push(child);
          }
        }

        const liRuns = [];
        for (const node of inlineNodes) {
          const runs = await processInlineNodes(node, { size: 22, font: "Calibri", color: "28283C" });
          liRuns.push(...runs);
        }

        const bulletSymbol = listLevel === 0 ? "\u2022\t" : listLevel === 1 ? "\u25CB\t" : "\u25AA\t";
        const prefix = isOrdered ? `${i + 1}.\t` : bulletSymbol;

        elements.push(
          new Paragraph({
            children: [
              new TextRun({
                text: prefix,
                size: 22,
                font: "Calibri",
                color: "28283C",
              }),
              ...liRuns,
            ],
            indent: {
              left: (listLevel + 1) * 360,
              hanging: 240,
            },
            spacing: { after: 60 },
          })
        );

        for (const nestedList of nestedLists) {
          await processList(nestedList, listLevel + 1);
        }
      }
    }

    if (tagName === "ul" || tagName === "ol") {
      await processList(el, 0);
      return;
    }

    if (tagName === "blockquote") {
      elements.push(
        new Paragraph({
          children: await processInlineNodes(el, { italic: true, color: "555566", size: 22 }),
          indent: { left: 720 },
          spacing: { before: 120, after: 120 },
        })
      );
      return;
    }

    if (tagName === "table") {
      const rows = Array.from(el.querySelectorAll("tr"));
      if (rows.length > 0) {
        const tableRows = [];
        for (const tr of rows) {
          const cells = Array.from(tr.querySelectorAll("th, td"));
          const isHeader = tr.querySelector("th") !== null;
          const tableCells = [];
          for (const cell of cells) {
            const cellRuns = await processInlineNodes(cell, {
              bold: isHeader,
              color: isHeader ? "3D2B90" : "28283C",
              size: 20,
            });
            tableCells.push(
              new TableCell({
                children: [
                  new Paragraph({
                    children: cellRuns.length > 0 ? cellRuns : [new TextRun("")],
                    spacing: { after: 60, before: 60 },
                  }),
                ],
                shading: isHeader ? { fill: "F3F1F9" } : undefined,
              })
            );
          }
          tableRows.push(new TableRow({ children: tableCells }));
        }

        elements.push(
          new Table({
            rows: tableRows,
            width: { size: 100, type: WidthType.PERCENTAGE },
          })
        );
      }
      return;
    }

    const hasBlockChildren = Array.from(el.children).some((c) =>
      ["div", "p", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "table", "blockquote", "img"].includes(
        c.tagName.toLowerCase()
      )
    );

    if (hasBlockChildren) {
      for (const child of Array.from(el.childNodes)) {
        await processBlockNode(child);
      }
    } else {
      const inlineRuns = await processInlineNodes(el);
      if (inlineRuns.length > 0) {
        elements.push(
          new Paragraph({
            children: inlineRuns,
            spacing: { after: 160 },
          })
        );
      }
    }
  }

  for (const child of Array.from(root.childNodes)) {
    await processBlockNode(child);
  }

  return elements.length > 0 ? elements : [new Paragraph({ children: [new TextRun("")] })];
}

/**
 * Exports HTML content as a genuine formatted Microsoft Word (.docx) document.
 * Compatible with Microsoft Word, Google Docs, Apple Pages, and LibreOffice.
 *
 * @param {string|string[]} htmlBlocks - Single HTML string or array of HTML strings
 * @param {string} filename - Target filename (e.g. Translation_2026-09-12_11-30.docx)
 */
export async function generateWordFromHtml(htmlBlocks, filename = "document.docx") {
  const blocks = Array.isArray(htmlBlocks) ? htmlBlocks : [htmlBlocks];
  const combinedContent = blocks.filter(Boolean).join("<br/><br/>");

  const docElements = await parseHtmlToDocxElements(combinedContent);

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1440, // 1 inch (1440 twips)
              right: 1440,
              bottom: 1440,
              left: 1440,
            },
          },
        },
        children: docElements,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const finalName = filename.replace(/\.docx?$/i, "") + ".docx";

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = finalName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

